import { FormEvent, useCallback, useRef, useState } from 'react'

import Image from 'next/image'
import { useRouter } from 'next/router'

import { ZodError, z } from 'zod'

import { useFoodMutation } from '~/api-client/food_api'
import { IntroductionLetter, User } from '~/api-definition/definition'
import { Button } from '~/components/action/Button'
import { Modal } from '~/components/action/Modal'
import { AppImage } from '~/components/data-display/AppImage'
import { TextArea } from '~/components/data-input/TextField'
import { getFormValueString } from '~/libs/form'
import { userProfileIconOpt } from '~/libs/user'

const FormSchema = z.object({
  title: z
    .string({ required_error: 'タイトルを入力してください' })
    .min(1, { message: 'タイトルを入力してください' })
    .max(60, { message: '60文字以内で入力してください' }),
  bodyText: z
    .string()
    .max(2000, { message: '2,000文字以内で入力してください' })
    .optional(),
})

export const IntroductionLetterForm: React.FC<
  | {
      toUser: User
      back?: () => void
    }
  | {
      letter: IntroductionLetter
      back?: () => void
    }
> = (props) => {
  const formRef = useRef<HTMLFormElement>(null)
  const router = useRouter()

  const [error, setError] = useState<ZodError<z.infer<typeof FormSchema>>>()
  const [triedSubmit, setTriedSubmit] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showDone, setShowDone] = useState(false)

  const newIntroductionLetter = useFoodMutation(
    '/user/new_introduction_letter',
    {
      onSuccess: () => {
        setShowDone(true)
      },
      onFinally: () => {
        setShowConfirmation(false)
      },
    }
  )

  const updateIntroductionLetter = useFoodMutation(
    '/user/update_introduction_letter',
    {
      onSuccess: () => {
        setShowDone(true)
      },
      onFinally: () => {
        setShowConfirmation(false)
      },
    }
  )

  const validateForm = useCallback(() => {
    if (formRef.current == null) return
    const formData = new FormData(formRef.current)
    const title = getFormValueString(formData, 'title')
    const bodyText = getFormValueString(formData, 'bodyText')
    const result = FormSchema.safeParse({ title, bodyText })
    if (!result.success) {
      setError(result.error)
      return
    }
    setError(undefined)
    return result.data
  }, [])

  const handleSubmit = useCallback((formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault()
    setTriedSubmit(true)
    setShowConfirmation(true)
  }, [])

  const handleConfirm = useCallback(() => {
    const validated = validateForm()
    if (validated == null) return
    if ('letter' in props) {
      updateIntroductionLetter({
        introductionLetterId: props.letter.introductionLetterId,
        title: validated.title,
        bodyText: validated.bodyText,
      })
    } else {
      newIntroductionLetter({
        title: validated.title,
        bodyText: validated.bodyText,
        userId: props.toUser.userId,
      })
    }
  }, [newIntroductionLetter, props, updateIntroductionLetter, validateForm])

  return (
    <>
      <Modal
        px="px-0"
        isOpen={showDone}
        title={
          <div className="grid grid-cols-[max-content_auto_max-content] w-full px-6">
            <Image
              src="/images/introduction_letters/thanks-title-left.png"
              width={31}
              height={47}
              alt=""
            />
            <p className="text-center w-full">
              紹介コメント
              <br />
              ありがとうございました！
            </p>
            <Image
              src="/images/introduction_letters/thanks-title-right.png"
              width={31}
              height={47}
              alt=""
            />
          </div>
        }
        body={
          <>
            <div className="bg-[#f8f3ea] h-[170px] flex flex-col gap-3 justify-center items-center mb-4">
              <Image
                src="/images/introduction_letters/thanks.png"
                width={157}
                height={45}
                alt=""
              />
              <AppImage
                variant={{
                  type: 'profile',
                  user: 'letter' in props ? props.letter.toUser : props.toUser,
                }}
                rounded="full"
                src={userProfileIconOpt(
                  'letter' in props ? props.letter.toUser : props.toUser
                )}
                width={70}
                height={70}
                alt=""
              />
            </div>
            <ul className="text-[13px] list-disc w-[290px] mx-auto">
              <li>
                紹介コメントは承認後にプロフィールに表示されるようになります{' '}
              </li>
              <li>
                設定画面（マイページ右上）から、紹介コメントの編集や削除ができます{' '}
              </li>
            </ul>
          </>
        }
        actionButtons={
          <div className="px-6 mt-6">
            <Button
              type="button"
              variant="functionWeak"
              className="w-[285px] mx-auto"
              onClick={async () => {
                setShowDone(false)
                ;(props.back ?? router.back)()
              }}
            >
              閉じる
            </Button>
          </div>
        }
      />
      <Modal
        isOpen={showConfirmation}
        setIsOpen={setShowConfirmation}
        title="紹介コメントを送りますか?"
        body={
          <ul className="text-[13px] list-disc w-[290px] mx-auto">
            <li>紹介コメントは相手の方が承認後に全体に公開されます</li>
            <li>
              相手の方が嫌な思いをしないように配慮したコメントをお願いします
            </li>
            <li>利用規約に違反した内容は削除の対象となります</li>
          </ul>
        }
        actionButtons={
          <div className="grid grid-cols-2 mt-8 gap-x-4">
            <Button
              type="button"
              className="w-full bg-gray"
              variant="functionWeak"
              shrink
              onClick={async () => {
                setShowConfirmation(false)
              }}
            >
              キャンセル
            </Button>
            <Button
              type="button"
              variant="primary"
              shrink
              onClick={handleConfirm}
            >
              OK
            </Button>
          </div>
        }
      />
      <form
        className="space-y-4"
        onSubmit={handleSubmit}
        ref={formRef}
        onChange={validateForm}
      >
        <TextArea
          required
          triedSubmit={triedSubmit}
          label="紹介コメント（タイトル）"
          error={error?.flatten().fieldErrors.title}
          inputProps={{
            name: 'title',
            rows: 3,
            defaultValue: 'letter' in props ? props.letter.title : undefined,
            placeholder:
              '例)とても礼儀正しくマナーがある方です。お話も面白く楽しく食事をさせていただきました！(最大60文字)',
          }}
        />
        <TextArea
          dynamicHeight
          textareaClassName="max-h-[50vh]"
          triedSubmit={triedSubmit}
          label="紹介コメント（詳細）"
          error={error?.flatten().fieldErrors.bodyText}
          inputProps={{
            name: 'bodyText',
            rows: 8,
            defaultValue: 'letter' in props ? props.letter.bodyText : undefined,
            placeholder:
              'この項目は必須ではありません。タイトルに書ききれなかった内容や、食事会でご一緒した際のエピソードなど、もっと詳しくご紹介したい場合にコメントを記載してください。(最大 2,000文字)',
          }}
        />

        <Button type="submit" variant="primary" className="px-4 mx-auto mt-3">
          紹介コメントを書く
        </Button>
      </form>
    </>
  )
}
